import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  usePrimaryComplaintStore,
  ComplaintHistoryStep,
  VisitReason,
} from "@/store/primaryComplaint";
import { ComplaintHistory1 } from "@/components/complaints/ComplaintHistory1";
import { ComplaintHistory2 } from "@/components/complaints/ComplaintHistory2";
import { ComplaintHistory3 } from "@/components/complaints/ComplaintHistory3";

/**
 * Complaint history wizard: step 1 (onset, mode, frequency) or step 2 (severity, sleep, work, quality, radiation).
 * Matches app-pwa ComplaintHistory.vue.
 * When there is no complaintId (e.g. user clicked Complaint History with no complaints), redirect to primary-complaint.
 */
export function ComplaintHistoryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const step = usePrimaryComplaintStore((s) => s.complaintHistory.step);
  const currentComplaintValues = usePrimaryComplaintStore((s) => s.currentComplaintValues);
  const setComplaintHistoryStep = usePrimaryComplaintStore((s) => s.setComplaintHistoryStep);
  const setVisitReason = usePrimaryComplaintStore((s) => s.setVisitReason);
  const setHumanBody = usePrimaryComplaintStore((s) => s.setHumanBody);

  const complaintId = searchParams.get("complaintId");
  const hasCheckedInitialStepReset = useRef(false);

  useEffect(() => {
    hasCheckedInitialStepReset.current = false;
  }, [complaintId]);

  useEffect(() => {
    if (!complaintId) {
      navigate("/complaint/primary-complaint", { replace: true });
      return;
    }
  }, [complaintId, navigate]);

  useEffect(() => {
    const complaintType = searchParams.get("complaintType");
    const region = searchParams.get("region");
    if (complaintType) {
      const normalized = complaintType.toLowerCase();
      if (Object.values(VisitReason).includes(normalized as VisitReason)) {
        setVisitReason(normalized as VisitReason);
      }
    }
    if (region) {
      setHumanBody({
        selectedPart: { partName: "", side: "", label: region },
        selectedInternalPart: region,
      });
    }
  }, [searchParams, setVisitReason, setHumanBody]);

  useEffect(() => {
    if (!complaintId || hasCheckedInitialStepReset.current) return;
    hasCheckedInitialStepReset.current = true;
    if (
      (step === ComplaintHistoryStep.ComplaintHistory2 || step === ComplaintHistoryStep.ComplaintHistory3) &&
      !currentComplaintValues?.length
    ) {
      setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory1);
    }
  }, [complaintId, step, currentComplaintValues, setComplaintHistoryStep]);

  if (!complaintId) {
    return null;
  }
  if (step === ComplaintHistoryStep.ComplaintHistory3) {
    return <ComplaintHistory3 />;
  }
  if (step === ComplaintHistoryStep.ComplaintHistory2) {
    return <ComplaintHistory2 />;
  }
  return <ComplaintHistory1 />;
}
